import React, { FC, useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import PlatformStatsFeatureFlagDetail from './PlatformStatsFeatureFlagDetail';
import {
  FlagListResponse,
  FlagSummary,
  STATUS_LABELS,
  apiErrorMessage,
  flagStatus,
  formatDate,
  formatJson,
} from './platformStatsFeatureFlagTypes';
import './platform-stats-feature-flags.scss';

/**
 * Platform Stats → Feature Flags. Manages the flags declared in dream-be
 * `lib/flags/registry.ts` (runtime default, kill switch, rollout, scoped rules),
 * and shows their change history. Flags cannot be created or archived here: a
 * flag exists while code declares it and is archived on boot once the registry
 * entry is deleted.
 */
const PlatformStatsFeatureFlags: FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState<FlagListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [filter, setFilter] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  const authHeaders = useCallback(
    async () => ({
      headers: { Authorization: `Bearer ${await getAccessTokenSilently()}` },
    }),
    [getAccessTokenSilently],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<FlagListResponse>(
        '/platform-stats/feature-flags',
        {
          ...(await authHeaders()),
          params: includeArchived ? { includeArchived: 1 } : {},
        },
      );
      setData(res.data);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load feature flags'));
    } finally {
      setLoading(false);
      setVersion((v) => v + 1);
    }
  }, [authHeaders, includeArchived]);

  useEffect(() => {
    void load();
  }, [load]);

  const flags = (data?.flags ?? []).filter((f) => {
    if (!filter.trim()) return true;
    const q = filter.trim().toLowerCase();
    const d = f.definition;
    return (
      f.key.toLowerCase().includes(q) ||
      (d?.description ?? f.state?.description ?? '')
        .toLowerCase()
        .includes(q) ||
      (d?.owner ?? f.state?.owner ?? '').toLowerCase().includes(q) ||
      (d?.category ?? f.state?.category ?? '').toLowerCase().includes(q)
    );
  });

  const selected = selectedKey
    ? ((data?.flags ?? []).find((f) => f.key === selectedKey) ?? null)
    : null;

  return (
    <div className='platform-stats-feature-flags'>
      <div className='header'>
        <div>
          <h2>Feature Flags</h2>
          <p className='subtitle'>
            Flags are declared in code (<code>lib/flags/registry.ts</code>);
            this page changes their runtime state. Every change needs a reason
            and is recorded in <code>feature_flag_history</code>. Archive a flag
            here once it is done; it is archived automatically when its registry
            entry is deleted.
          </p>
        </div>
        <div className='header-actions'>
          <input
            type='search'
            placeholder='Filter by key, owner, category…'
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            aria-label='Filter flags'
          />
          <label
            className='checkbox'
            title='Archived flags are hidden from this list and read-only; everyone gets the code default until they are unarchived.'
          >
            <input
              type='checkbox'
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            Show archived
          </label>
          <button className='ghost-btn' onClick={() => void load()}>
            Refresh
          </button>
        </div>
      </div>

      {error && <div className='notice error'>{error}</div>}

      {loading && !data ? (
        <div className='loading'>Loading flags…</div>
      ) : (
        <div className='table-wrap'>
          <table>
            <thead>
              <tr>
                <th>Key</th>
                <th>Kind</th>
                <th>Status</th>
                <th>Default</th>
                <th>Rules</th>
                <th>Owner</th>
                <th>Category</th>
                <th>Expires</th>
                <th>Last change</th>
              </tr>
            </thead>
            <tbody>
              {flags.length === 0 && (
                <tr>
                  <td colSpan={9} className='empty'>
                    {data?.flags.length
                      ? 'No flags match the filter.'
                      : 'No flags registered.'}
                  </td>
                </tr>
              )}
              {flags.map((flag: FlagSummary) => {
                const status = flagStatus(flag);
                const s = flag.state;
                const d = flag.definition;
                return (
                  <tr
                    key={flag.key}
                    className={`flag-row ${flag.key === selectedKey ? 'selected' : ''}`}
                    onClick={() => setSelectedKey(flag.key)}
                  >
                    <td className='key'>
                      <button
                        className='link-btn'
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedKey(flag.key);
                        }}
                      >
                        {flag.key}
                      </button>
                      {!flag.inRegistry && (
                        <span className='chip warn'>not in code</span>
                      )}
                      <div className='description'>
                        {d?.description ?? s?.description ?? ''}
                      </div>
                    </td>
                    <td>{s?.kind ?? d?.kind ?? '—'}</td>
                    <td>
                      <span className={`chip status-${status}`}>
                        {STATUS_LABELS[status]}
                        {status === 'rollout' && s?.rolloutPercent != null
                          ? ` ${s.rolloutPercent}%`
                          : ''}
                      </span>
                    </td>
                    <td className='mono'>
                      {s ? formatJson(s.defaultValue) : formatJson(d?.default)}
                    </td>
                    <td>{flag.rules.length}</td>
                    <td>{d?.owner ?? s?.owner ?? '—'}</td>
                    <td>{d?.category ?? s?.category ?? '—'}</td>
                    <td>{formatDate(d?.expiresAt ?? s?.expiresAt)}</td>
                    <td>
                      {s?.updatedBy ? (
                        <>
                          <div>{formatDate(s.updatedAt)}</div>
                          <div className='muted'>{s.updatedBy}</div>
                        </>
                      ) : (
                        formatDate(s?.updatedAt)
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && data && (
        <PlatformStatsFeatureFlagDetail
          key={selected.key}
          flagKey={selected.key}
          scopes={data.scopes}
          bucketKeys={data.bucketKeys}
          version={version}
          onChanged={() => void load()}
          onClose={() => setSelectedKey(null)}
        />
      )}
    </div>
  );
};

export default PlatformStatsFeatureFlags;
